import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }

        const message = error.response?.data?.message || error.message || 'An error occurred';
        toast.error(message);

        return Promise.reject(error);
    }
);

export const authAPI = {
    register: async (userData) => {
        const response = await api.post('/auth/register', userData);
        return response.data;
    },

    login: async (credentials) => {
        const response = await api.post('/auth/login', credentials);
        return response.data;
    },

    getReferralCodes: async () => {
        const response = await api.get('/auth/referral-codes');
        return response.data;
    },

    refreshToken: async () => {
        const response = await api.post('/auth/refresh-token');
        return response.data;
    },

    logout: async () => {
        const response = await api.post('/auth/logout');
        return response.data;
    },

    getProfile: async () => {
        const response = await api.get('/auth/profile');
        return response.data;
    },

    forgotPassword: async (email) => {
        const response = await api.post('/auth/forgot-password', { email });
        return response.data;
    },

    resetPassword: async (data) => {
        const response = await api.post('/auth/reset-password', data);
        return response.data;
    },
};

export const cartAPI = {
    getCart: async () => {
        const response = await api.get('/cart');
        return response.data;
    },

    addToCart: async (productId, quantity = 1, variant = null) => {
        const response = await api.post('/cart/add', { productId, quantity, variant });
        return response.data;
    },

    updateCartItem: async (productId, quantity) => {
        const response = await api.put('/cart/update', { productId, quantity });
        return response.data;
    },

    removeFromCart: async (productId) => {
        const response = await api.delete(`/cart/remove/${productId}`);
        return response.data;
    },

    clearCart: async () => {
        const response = await api.delete('/cart/clear');
        return response.data;
    },
};

export const productAPI = {
    getAllProducts: async (params = {}) => {
        const response = await api.get('/products', { params });
        return response.data;
    },

    getProductById: async (id) => {
        const response = await api.get(`/products/${id}`);
        return response.data;
    },

    getCategories: async () => {
        const response = await api.get('/products/categories');
        return response.data;
    },

    // Admin-only
    getAdminProducts: async () => {
        const response = await api.get('/products/admin/all');
        return response.data;
    },

    createProduct: async (productData) => {
        const response = await api.post('/products', productData);
        return response.data;
    },

    updateProduct: async (id, productData) => {
        const response = await api.put(`/products/${id}`, productData);
        return response.data;
    },

    deleteProduct: async (id) => {
        const response = await api.delete(`/products/${id}`);
        return response.data;
    },
};


export const userAPI = {
    updateProfile: async (profileData) => {
        const response = await api.put('/auth/profile', profileData);
        return response.data;
    },
    getMyOrders: async () => {
        const response = await api.get('/orders/my');
        return response.data;
    },
};

export const ordersAPI = {
    create: async (orderData) => {
        const response = await api.post('/orders', orderData);
        return response.data;
    },
    myOrders: async () => {
        const response = await api.get('/orders/my');
        return response.data;
    },
    // admin
    listAll: async () => {
        const response = await api.get('/orders');
        return response.data;
    },
    updateStatus: async (orderId, status, paymentCollectedAs) => {
        const body = { status };
        if (paymentCollectedAs) body.paymentCollectedAs = paymentCollectedAs;
        const response = await api.put(`/orders/${orderId}/status`, body);
        return response.data;
    },
    bulkAccept: async (date) => {
        const response = await api.post('/orders/bulk-accept', { date });
        return response.data;
    },
};

export const adminAPI = {
    listUsers: async () => {
        const response = await api.get('/admin/users');
        return response.data;
    },
    listOrders: async () => {
        const response = await api.get('/admin/orders');
        return response.data;
    },
    updateUserGroup: async (userId, group) => {
        const response = await api.put(`/admin/users/${userId}/group`, { group });
        return response.data;
    },
    listGroupAdmins: async () => {
        const response = await api.get('/admin/group-admins');
        return response.data;
    },
    assignGroupAdmin: async (userId, payload) => {
        const response = await api.put(`/admin/users/${userId}/group-admin`, payload);
        return response.data;
    },
};

export const deliveryLogAPI = {
    list: async (params = {}) => {
        const response = await api.get('/delivery-logs', { params });
        return response.data;
    },
    create: async (payload) => {
        const response = await api.post('/delivery-logs', payload);
        return response.data;
    },
    remove: async (id) => {
        const response = await api.delete(`/delivery-logs/${id}`);
        return response.data;
    },
};

export const categoryAPI = {
    getAllCategories: async () => {
        const response = await api.get('/categories');
        return response.data;
    },

    getCategoryById: async (id) => {
        const response = await api.get(`/categories/${id}`);
        return response.data;
    },

    createCategory: async (categoryData) => {
        const response = await api.post('/categories', categoryData);
        return response.data;
    },

    updateCategory: async (id, categoryData) => {
        const response = await api.put(`/categories/${id}`, categoryData);
        return response.data;
    },

    deleteCategory: async (id) => {
        const response = await api.delete(`/categories/${id}`);
        return response.data;
    },

    uploadCategoryImage: async (imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);
        const response = await api.post('/categories/upload-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },
};

export const homeBannerAPI = {
    getActive: async () => {
        const response = await api.get('/home-banners/active');
        return response.data;
    },
    listAll: async () => {
        const response = await api.get('/home-banners/admin/all');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/home-banners', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/home-banners/${id}`, data);
        return response.data;
    },
    remove: async (id) => {
        const response = await api.delete(`/home-banners/${id}`);
        return response.data;
    },
    uploadImage: async (imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);
        const response = await api.post('/home-banners/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const discountHeroAPI = {
    getActive: async () => {
        const response = await api.get('/discount-hero/active');
        return response.data;
    },
    // Admin-only helpers
    listAll: async () => {
        const response = await api.get('/discount-hero/admin/all');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/discount-hero', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/discount-hero/${id}`, data);
        return response.data;
    },
    remove: async (id) => {
        const response = await api.delete(`/discount-hero/${id}`);
        return response.data;
    },
    uploadImage: async (imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);
        const response = await api.post('/discount-hero/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export const videoAPI = {
    getActive: async () => {
        const response = await api.get('/videos/active');
        return response.data;
    },
    listAll: async () => {
        const response = await api.get('/videos/admin/all');
        return response.data;
    },
    create: async (payload) => {
        const response = await api.post('/videos', payload);
        return response.data;
    },
    update: async (id, payload) => {
        const response = await api.put(`/videos/${id}`, payload);
        return response.data;
    },
    remove: async (id) => {
        const response = await api.delete(`/videos/${id}`);
        return response.data;
    },
};

export const sharePostAPI = {
    getActive: async () => {
        const response = await api.get('/share-posts/active');
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/share-posts/${id}`);
        return response.data;
    },
    listAll: async () => {
        const response = await api.get('/share-posts/admin/all');
        return response.data;
    },
    create: async (payload) => {
        const response = await api.post('/share-posts', payload);
        return response.data;
    },
    update: async (id, payload) => {
        const response = await api.put(`/share-posts/${id}`, payload);
        return response.data;
    },
    remove: async (id) => {
        const response = await api.delete(`/share-posts/${id}`);
        return response.data;
    },
    uploadImage: async (imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);
        const response = await api.post('/share-posts/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};